<template>
  <el-dialog
    id="edit-modal"
    class="form-modal"
    :visible.sync="editVisible"
    v-loading="editProcessing"
    :fullscreen="true"
    :show-close="false"
    :modal="false"
  >
    <div id="edit-modal-header" class="modal-header">
      <div class="modal-header-layout flex grid-6 align-center">
        <h1 class="modal-title col-2">View / Edit Feature Code</h1>
        <div class="modal-actions col-2">
          <el-button v-if="this.editForm.tenantId && this.editForm.tenantId !== 0" type="primary" @click="editOperation" :disabled="codeUsed"
            >Save</el-button
          >
          <el-button
            type="default"
            class="hidden-xs-only"
            @click="cancelOperation"
            >Cancel</el-button
          >
          <el-button
            type="default"
            icon="el-icon-close"
            class="hidden-sm-and-up"
            @click="cancelOperation"
          ></el-button>
        </div>
      </div>
    </div>

    <el-form>
      <recent-audits service="Feature Code" :record="selectedFeature"/>
      <div class="inline-form-group">
        <el-col :span="13">
          <el-input v-model="code" placeholder="Code">
            <template slot="prefix">Code</template>
            <template v-if="codeUsed" slot="append">
              <in-use :inUse="codeInUse" />
            </template>
          </el-input>
        </el-col>
        <el-col :span="1" />
        <el-col :span="10">
          <el-select v-model="numvar" placeholder="Variable" clearable>
            <el-option
              v-for="(numVar, idx) in numvarOptions"
              :key="idx"
              :value="numVar"
              :label="numVar"
            />
            <template slot="prefix">Variable</template>
          </el-select>
        </el-col>
      </div>
      <div class="inline-form-group">
        <el-tag class="mb-10" type="warning">
          <i class="el-icon-warning" style="margin-right: 4px;"></i>
          Feature code must not match #0, #1, ## &amp; #* and must start with
          either # or *.
        </el-tag>
      </div>

      <el-input v-model="editForm.comment" placeholder="Enter description">
        <template slot="prefix">Description</template>
      </el-input>

      <div class="mt-20">
        <h2 class="form-section-header">When this code is dialed</h2>
        <div class="inline-form-group">
          <el-col :span="24">
            <destination-selector
              :draggable="true"
              :showHeader="false"
              :draggableDestTypes="draggableDestTypes"
              :include-pharmacy-dests="true"
                :include-pharmacy-agents="true"
              :selectedDestinations="editForm.destinations"
              :clearFields="clearFormFields"
              @destinationsSelected="onDestinationsSelection"
              title=""
            />
          </el-col>
        </div>
      </div>
    </el-form>
  </el-dialog>
</template>

<script>
import DestinationSelector from "@/components/DestinationSelector.vue";
import RecentAudits from "@/components/RecentAudits";
import InUse from "@/components/InUse";

export default {
  name: "FeaturesCodeEditForm",

  props: {
    editVisible: { required: true },
    codeInUse: { required: true },
    draggableDestTypes: { required: true },
    clearFormFields: { required: true },
    editProcessing: { required: true },
    selectedFeature: { required: true }
  },

  components: {
    DestinationSelector,
    RecentAudits,
    InUse
  },

  data() {
    return {
      userChangedFeatureCode: false,
      userChangedNumVar: false,
      initialFeatureCode: "",
      numvar: "",
      code: "",
      numvarOptions: ["EXT", "NUM"],
      editForm: {
        tenantId: null,
        code: "",
        numvar: "",
        comment: "",
        destinations: []
      }
    };
  },
  computed: {
    selectedTenant() {
      return this.$store.state.selectedTenant;
    },

    codeUsed() {
      return this.codeInUse && this.codeInUse.inUse;
    }
  },

  watch: {
    clearFormFields(value) {
      if (value) {
        this.clearFields();
      }
    },

    selectedFeature(val) {
      const { code, comment, destinations, tenantId } = val;
      this.editForm.comment = comment;
      this.editForm.tenantId = tenantId;
      this.initialFeatureCode = code;
      const codeArr = code.split("[");
      this.code = codeArr[0];
      if (codeArr.length === 2) {
        this.numvar = codeArr[1].substr(0, codeArr[1].length - 1);
      }

      if (destinations && destinations.length) {
        this.editForm.destinations = [...destinations];
      }
    },

    code(val) {
      this.editForm.code = val;
      if (!this.userChangedFeatureCode) {
        this.userChangedFeatureCode = true;
        return;
      }

      const featureCode = `${val}${this.numvar ? "[" + this.numvar + "]" : ""}`;

      if (featureCode !== this.initialFeatureCode) {
        this.$emit("checkFeatureCode", featureCode);
      } else {
        this.$emit("checkFeatureCode", "*");
      }
    },

    numvar(val) {
      this.editForm.numvar = val;
      if (!this.userChangedNumVar) {
        this.userChangedNumVar = true;
        return;
      }

      const featureCode = `${this.code}[${val}]`;

      if (featureCode !== this.initialFeatureCode) {
        this.$emit("checkFeatureCode", featureCode);
      } else {
        this.$emit("checkFeatureCode", "*");
      }
    }
  },

  methods: {
    onDestinationsSelection(destinations) {
      this.editForm.destinations = [...destinations];
    },

    editOperation() {
      const editBody = { ...this.editForm };
      editBody.tenantId = this.selectedTenant.id;

      if (
        editBody.destinations.length > 1 &&
        editBody.destinations.find(dst => !dst.id)
      ) {
        this.$notify({
          title: "Empty Destination field",
          message: "Don't leave any destination field to be empty.",
          type: "error"
        });
        return;
      }

      if (
        editBody.destinations.length === 1 &&
        editBody.destinations[0].id === null
      ) {
        editBody.destinations = [];
      } else {
        editBody.destinations.forEach((dst, idx) => (dst.order = idx + 1));
      }

      this.$emit("editOperation", editBody);
    },

    cancelOperation() {
      this.$emit("cancelOperation", "edit");
    },

    clearFields() {
      this.initialFeatureCode = "";
      this.userChangedFeatureCode = false;
      this.userChangedNumVar = false;
      this.numvar = "";
      this.code = "";

      this.editForm = {
        tenantId: null,
        code: "",
        comment: "",
        numvar: "",
        destinations: []
      };

      this.$emit("formsCleared");
    },

    remoteMethodForAvailableNums(query) {
      this.$emit("remoteMethodForAvailableNums", query);
    }
  }
};
</script>
